import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { flagUrl } from "@/lib/countries";
import { fmtUAE, TZ_LABEL } from "@/lib/dates";
import { Lock, CheckCircle2, Goal } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const fmtDate = (iso) => `${fmtUAE(iso)} ${TZ_LABEL}`;

export default function MatchCard({ match, prediction, onUpdated }) {
  const [home, setHome] = useState(prediction?.home_score ?? "");
  const [away, setAway] = useState(prediction?.away_score ?? "");
  const [btts, setBtts] = useState(prediction?.btts ?? null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setHome(prediction?.home_score ?? "");
    setAway(prediction?.away_score ?? "");
    setBtts(prediction?.btts ?? null);
  }, [prediction?.id]);

  const isFinished = match.status === "finished";
  const now = new Date();
  const kickoffDt = new Date(match.kickoff);
  // NOTE: keep in sync with backend MATCH_LOCK_HOURS in /app/backend/server.py
  const MATCH_LOCK_HOURS = 2;
  const lockDt = new Date(kickoffDt.getTime() - MATCH_LOCK_HOURS * 60 * 60 * 1000);
  const kickoffPast = kickoffDt <= now;
  const locked = isFinished || lockDt <= now;
  // Format the time remaining
  const msToLock = lockDt - now;
  let lockLabel = null;
  if (!isFinished && !locked && msToLock > 0) {
    const hours = Math.floor(msToLock / 3600000);
    const mins = Math.floor((msToLock % 3600000) / 60000);
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      lockLabel = `Locks in ${days}d ${hours % 24}h`;
    } else if (hours >= 1) {
      lockLabel = `Locks in ${hours}h ${mins}m`;
    } else {
      lockLabel = `Locks in ${mins}m`;
    }
  }

  const submit = async () => {
    if (home === "" || away === "" || btts === null) {
      toast.error("Pick the score AND BTTS");
      return;
    }
    setSaving(true);
    try {
      await api.post("/predictions", {
        match_id: match.id,
        home_score: Number(home),
        away_score: Number(away),
        btts: btts,
      });
      toast.success("Prediction locked in. Good luck.");
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const scorePts = prediction?.score_points ?? 0;
  const winnerPts = prediction?.winner_points ?? 0;
  const bttsPts = prediction?.btts_points ?? 0;
  const totalPts = prediction?.points ?? 0;

  // Derived winner of predicted score
  const pickedWinner = home !== "" && away !== ""
    ? (Number(home) > Number(away) ? "home" : Number(away) > Number(home) ? "away" : "draw")
    : null;

  const pickWinner = (outcome) => {
    if (locked) return;
    const h = home === "" ? 0 : Number(home);
    const a = away === "" ? 0 : Number(away);
    if (outcome === "home") {
      if (h > a) return; // already a home win
      setHome(String(Math.max(1, a + 1)));
      if (away === "") setAway("0");
    } else if (outcome === "away") {
      if (a > h) return;
      setAway(String(Math.max(1, h + 1)));
      if (home === "") setHome("0");
    } else if (outcome === "draw") {
      const v = Math.max(h, a, 1);
      setHome(String(v));
      setAway(String(v));
    }
  };

  // Smart BTTS auto-lock based on the entered score
  useEffect(() => {
    if (locked) return;
    const h = home === "" ? null : Number(home);
    const a = away === "" ? null : Number(away);
    if (h === null || a === null) return;
    const mustBttsYes = h > 0 && a > 0;
    const mustBttsNo = h === 0 || a === 0;
    if (mustBttsYes && btts !== true) setBtts(true);
    else if (mustBttsNo && btts !== false) setBtts(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [home, away, locked]);

  const hN = home === "" ? null : Number(home);
  const aN = away === "" ? null : Number(away);
  const bttsYesDisabled = locked || (hN !== null && aN !== null && (hN === 0 || aN === 0));
  const bttsNoDisabled  = locked || (hN !== null && aN !== null && hN > 0 && aN > 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative overflow-hidden bg-surface border border-white/5 rounded-2xl p-6 hover:border-purple/60 hover:shadow-[0_8px_30px_rgba(160,64,192,0.22)] transition-all duration-300 card-lift"
      data-testid={`match-card-${match.id}`}
    >
      {/* Status row */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          {match.group && (
            <span className="text-[10px] tracking-[0.3em] uppercase px-2 py-1 rounded-full bg-vivid/15 text-lavender border border-vivid/40">
              Group {match.group}
            </span>
          )}
          <span className="text-[10px] tracking-[0.3em] uppercase text-white/40">
            {match.stage}
          </span>
        </div>
        <span className={`text-[10px] tracking-[0.25em] uppercase px-2 py-1 rounded-full ${
          isFinished
            ? "bg-lavender/10 text-lavender border border-lavender/30"
            : locked
              ? "bg-white/5 text-white/50 border border-white/10"
              : "bg-purple/15 text-purple border border-purple/40"
        }`}>
          {isFinished ? "Final" : locked ? (kickoffPast ? "Live" : "Locked") : "Upcoming"}
        </span>
      </div>

      {/* Countdown banner */}
      {lockLabel && (
        <div className="mb-4 -mt-1 text-[10px] tracking-[0.25em] uppercase text-lavender/90 text-center" data-testid={`lock-countdown-${match.id}`}>
          ⏱ {lockLabel}
        </div>
      )}

      {/* Teams */}
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 mb-6">
        <Team code={match.home_code} name={match.home_team} />
        <div className="text-center">
          {isFinished ? (
            <div className="font-mono text-3xl font-bold tracking-tight">
              {match.home_score}<span className="text-white/30 mx-1">–</span>{match.away_score}
            </div>
          ) : (
            <div className="font-display text-purple text-3xl">VS</div>
          )}
          <div className="text-[10px] tracking-[0.2em] text-white/40 mt-1">
            {fmtDate(match.kickoff)}
          </div>
        </div>
        <Team code={match.away_code} name={match.away_team} />
      </div>

      {/* Prediction zone */}
      <div className="border-t border-white/5 pt-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[10px] tracking-[0.3em] uppercase text-white/40">
            Your Predictions
          </span>
          {prediction && isFinished && (
            <span className="text-xs font-bold tracking-wider uppercase px-2 py-1 rounded-full bg-lavender/15 text-lavender border border-lavender/30">
              +{totalPts} total
            </span>
          )}
        </div>

        {/* Winner picker */}
        <div className="mb-3">
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-2 text-center">
            Pick The Outcome
          </div>
          <div className="grid grid-cols-3 gap-2">
            <WinnerBtn
              label={match.home_team.slice(0, 12)}
              sub="Home win"
              active={pickedWinner === "home"}
              disabled={locked}
              onClick={() => pickWinner("home")}
              testid={`winner-home-${match.id}`}
            />
            <WinnerBtn
              label="Draw"
              sub="Level"
              active={pickedWinner === "draw"}
              disabled={locked}
              onClick={() => pickWinner("draw")}
              testid={`winner-draw-${match.id}`}
            />
            <WinnerBtn
              label={match.away_team.slice(0, 12)}
              sub="Away win"
              active={pickedWinner === "away"}
              disabled={locked}
              onClick={() => pickWinner("away")}
              testid={`winner-away-${match.id}`}
            />
          </div>
        </div>

        {/* Score inputs */}
        <div className="flex items-center justify-center gap-3 mb-4">
          <ScoreInput value={home} onChange={setHome} disabled={locked} testid={`home-score-${match.id}`} />
          <div className="font-display text-2xl text-white/40">:</div>
          <ScoreInput value={away} onChange={setAway} disabled={locked} testid={`away-score-${match.id}`} />
        </div>

        {/* BTTS picker */}
        <div className="mb-4">
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-2 text-center flex items-center justify-center gap-1.5">
            <Goal size={11}/> Both Teams To Score?
          </div>
          <div className="flex gap-2 justify-center">
            <BttsBtn label="Yes" active={btts === true} onClick={() => !bttsYesDisabled && setBtts(true)} disabled={bttsYesDisabled} testid={`btts-yes-${match.id}`} />
            <BttsBtn label="No" active={btts === false} onClick={() => !bttsNoDisabled && setBtts(false)} disabled={bttsNoDisabled} testid={`btts-no-${match.id}`} />
          </div>
        </div>

        {/* Award badges if finished */}
        {prediction && isFinished && (
          <div className="flex items-center justify-center gap-2 mb-4 flex-wrap">
            <Pill ok={scorePts === 3}>
              {scorePts === 3 ? "+3 Exact" : "Exact · 0"}
            </Pill>
            <Pill mid={winnerPts > 0}>
              {winnerPts > 0 ? `+${winnerPts} Winner` : "Winner · 0"}
            </Pill>
            <Pill ok={bttsPts === 1}>
              {bttsPts === 1 ? "+1 BTTS" : "BTTS · 0"}
            </Pill>
          </div>
        )}

        {/* CTA */}
        <div>
          {locked ? (
            <div className="flex items-center justify-center gap-2 text-xs text-white/40 uppercase tracking-widest">
              <Lock size={12} />
              {isFinished ? "Match Finished" : "Predictions Locked"}
            </div>
          ) : (
            <button
              onClick={submit}
              disabled={saving}
              data-testid={`predict-btn-${match.id}`}
              className="cta-sweep btn-bounce w-full bg-gradient-to-r from-vivid to-purple text-white font-bold uppercase tracking-wide rounded-full px-6 py-3 hover:from-purple hover:to-lavender hover:text-plum transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-[0_4px_18px_rgba(160,64,192,0.35)]"
            >
              {prediction ? <><CheckCircle2 size={16}/> Update Prediction</> : "Lock It In"}
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function Team({ code, name }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <img
        src={flagUrl(code)}
        alt={name}
        className="w-16 h-16 rounded-full object-cover ring-2 ring-white/10 shadow-lg"
      />
      <div className="text-center">
        <div className="font-display text-base sm:text-lg tracking-wider uppercase leading-tight mt-1 max-w-[140px]">
          {name}
        </div>
      </div>
    </div>
  );
}

function ScoreInput({ value, onChange, disabled, testid }) {
  return (
    <input
      type="number"
      min="0"
      max="30"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      data-testid={testid}
      className="w-16 h-16 text-center text-3xl font-mono font-bold bg-black/40 border border-white/15 rounded-xl focus:border-purple focus:ring-2 focus:ring-purple/40 outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
      placeholder="-"
    />
  );
}

function BttsBtn({ label, active, onClick, disabled, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`px-6 py-2 rounded-full text-xs uppercase tracking-[0.2em] font-bold transition-all ${
        active
          ? "bg-lavender text-plum border border-lavender shadow-[0_0_18px_rgba(232,180,240,0.4)]"
          : "bg-black/30 text-white/60 border border-white/10 hover:border-purple hover:text-white"
      } disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {label}
    </button>
  );
}

function Pill({ ok, mid, children }) {
  const cls = ok ? "bg-lavender/20 text-lavender border-lavender/40"
    : mid ? "bg-purple/15 text-purple border-purple/40"
    : "bg-white/5 text-white/40 border-white/10";
  return (
    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${cls}`}>
      {children}
    </span>
  );
}

function WinnerBtn({ label, sub, active, onClick, disabled, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`btn-bounce px-3 py-2 rounded-xl text-center transition-all border ${
        active
          ? "bg-gradient-to-br from-vivid to-purple text-white border-purple shadow-[0_0_18px_rgba(160,64,192,0.45)]"
          : "bg-black/30 text-white/60 border-white/10 hover:border-purple hover:text-white"
      } disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      <div className="text-xs font-display tracking-wider uppercase truncate">{label}</div>
      <div className="text-[9px] tracking-[0.25em] uppercase opacity-70 mt-0.5">{sub}</div>
    </button>
  );
}
